import { useState, useEffect, useRef } from 'react';
import { X, Zap, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const SESSION_KEY = 'ortem_exit_intent_shown';

export const ExitIntentPopup = () => {
    const [visible, setVisible] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const triggered = useRef(false);

    useEffect(() => {
        // Exit intent trigger (Desktop only - detects movement toward top bar)
        const handleMouseMove = (e: MouseEvent) => {
            if (e.clientY <= 20 && !triggered.current && window.innerWidth >= 768) {
                if (sessionStorage.getItem(SESSION_KEY)) return;
                triggered.current = true;
                setTimeout(() => setVisible(true), 200);
            }
        };

        // Manual trigger event (Any device)
        const handleManualTrigger = () => {
            setVisible(true);
        };

        // NOTE: the automatic mobile (delay-based) trigger was removed so the
        // popup never auto-appears on phones. Desktop exit-intent (above) stays.
        // The manual "open-estimate-popup" event still works on all devices, so
        // tapping a "Get a Free Estimate" CTA opens it intentionally.

        document.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('open-estimate-popup', handleManualTrigger);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('open-estimate-popup', handleManualTrigger);
        };
    }, []);

    const close = () => {
        setVisible(false);
        sessionStorage.setItem(SESSION_KEY, '1');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name || !email) return;

        setIsSubmitting(true);

        try {
            const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;

            if (!accessKey) {
                // Fallback to mailto if key is missing (though it should be there)
                const subject = encodeURIComponent(`Free Project Estimate Request from ${name}`);
                const body = encodeURIComponent(
                    `Hi Ortem Team,\n\nI'd like to get a free project estimate.\n\nName: ${name}\nEmail: ${email}\n\nPlease get back to me within 24 hours.\n\nThank you!`
                );
                window.location.href = `mailto:sales@ortemtech.com?subject=${subject}&body=${body}`;
                close();
                return;
            }

            const response = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    access_key: accessKey,
                    name: name,
                    email: email,
                    subject: `New Project Estimate Request - ${name}`,
                    from_name: "Ortem Tech Website",
                    message: `Customer Name: ${name}\nCustomer Email: ${email}\nAction: Requesting Project Estimate in 24 Hours.`,
                }),
            });

            const result = await response.json();

            if (result.success) {
                setIsSuccess(true);
                sessionStorage.setItem(SESSION_KEY, '1');
                setTimeout(() => {
                    setVisible(false);
                }, 3000);
            }
        } catch (error) {
            console.error("Form submission error:", error);
            // Fallback for safety
            window.location.href = `mailto:sales@ortemtech.com?subject=Project Estimate Request&body=Name: ${name}%0AEmail: ${email}`;
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!visible) return null;

    return (
        <div
            className="fixed inset-0 z-[999] flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) close(); }}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Modal */}
            <div className="relative z-10 w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">

                {/* Close */}
                {!isSuccess && (
                    <button
                        onClick={close}
                        disabled={isSubmitting}
                        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/80 transition-colors z-10 disabled:opacity-50"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                )}

                {/* Header band */}
                <div className="bg-gradient-to-r from-primary to-accent px-6 py-5 text-white">
                    <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase tracking-widest opacity-90">Before you go…</span>
                    </div>
                    <h2 className="font-display text-xl font-bold">Get a Free Project Estimate in 24 Hours</h2>
                    <p className="text-sm opacity-85 mt-1">No commitment. No sales pressure. Just a clear number.</p>
                </div>

                <div className="px-6 py-5">
                    {isSuccess ? (
                        <div className="py-8 text-center animate-in fade-in zoom-in duration-300">
                            <div className="w-16 h-16 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8" />
                            </div>
                            <h3 className="font-display text-lg font-bold text-foreground mb-2">Request Sent!</h3>
                            <p className="text-sm text-muted-foreground">
                                Thank you, {name}. We've received your request and our team will be in touch within 24 hours.
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Value props */}
                            <ul className="space-y-2 mb-5">
                                {[
                                    'Tailored to your specific idea or problem',
                                    'Includes timeline & technology recommendation',
                                    'Reply within 24 business hours, guaranteed',
                                ].map(t => (
                                    <li key={t} className="flex items-start gap-2 text-sm text-muted-foreground">
                                        <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                        {t}
                                    </li>
                                ))}
                            </ul>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className="space-y-3">
                                <input
                                    type="text"
                                    name="botcheck"
                                    style={{ display: 'none' }}
                                />
                                <input
                                    type="text"
                                    placeholder="Your name"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    required
                                    disabled={isSubmitting}
                                    className="w-full h-10 px-4 rounded-lg bg-secondary/50 border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground/60 disabled:opacity-50"
                                />
                                <input
                                    type="email"
                                    placeholder="Work email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                    disabled={isSubmitting}
                                    className="w-full h-10 px-4 rounded-lg bg-secondary/50 border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-muted-foreground/60 disabled:opacity-50"
                                />
                                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            Sending...
                                            <Clock className="w-4 h-4 ml-2 animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            Send Me a Free Estimate
                                            <ArrowRight className="w-4 h-4 ml-2" />
                                        </>
                                    )}
                                </Button>
                            </form>

                            {/* Dismiss quietly */}
                            <div className="flex items-center justify-between mt-4">
                                <button
                                    onClick={close}
                                    disabled={isSubmitting}
                                    className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors disabled:opacity-50"
                                >
                                    No thanks, I'll figure it out myself
                                </button>
                                <div className="flex items-center gap-1 text-xs text-muted-foreground/60">
                                    <Clock className="w-3 h-3" />
                                    <span>~24hr reply</span>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
